import StockDamageService from "services/StockDamageService";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import { db } from "helpers/DBHelper";
import { COLLECTIONS, DRAFT_STATUS } from "const";
import toast from "react-hot-toast";
import * as ShiftHelper from "helpers/ShiftHelper";

const saveStockDamageToCloudAndLocal = async (damageItems) => {
  if (!damageItems || damageItems.length === 0) {
    throw new Error("Please add at least one item");
  }

  const currentShiftId = ShiftHelper.getCurrentShiftId()?.id || null;

  const payload = {
    date: DateTimeHelpers.convertDateToDMY(new Date()),
    damages: damageItems.map((item) => ({
      item: item.productId,
      plu: Number(item.plu || 0),
      item_name: item.name,
      quantity: Number(item.qty),
      rate: item.price,
    })),
  };

  const response = await StockDamageService.createStockDamage(payload);

  for (const item of damageItems) {
    const remoteItem = response?.find((r) => r.item?.id === item.productId);

    const dbPayload = {
      date: item.date || DateTimeHelpers.getLocalISOString(),
      shift_id: currentShiftId,
      id: remoteItem?.item?.id || null,
      productId: item.productId,
      plu: item.plu,
      name: item.name,
      qty: item.qty,
      price: item.price,
      payable: item.payable,
      category: item.category,
      status: DRAFT_STATUS.SAVED,
    };

    if (item.local_id) {
      dbPayload.local_id = item.local_id;
      await db.update(COLLECTIONS.STOCK_DAMAGES.name, item.local_id, dbPayload);
    } else {
      await db.add(COLLECTIONS.STOCK_DAMAGES.name, dbPayload);
    }
  }

  return response;
};

const saveDraftStockDamage = async (damageItems) => {
  if (!damageItems || damageItems.length === 0) {
    throw new Error("Please add at least one item");
  }

  const currentShiftId = ShiftHelper.getCurrentShiftId()?.id || null;

  for (const item of damageItems) {
    await db.add(COLLECTIONS.STOCK_DAMAGES.name, {
      date: DateTimeHelpers.getLocalISOString(),
      shift_id: currentShiftId,
      id: null,
      productId: item.productId,
      plu: item.plu,
      name: item.name,
      qty: item.qty,
      price: item.price,
      payable: item.payable,
      category: item.category,
      status: DRAFT_STATUS.DRAFT,
    });
  }
};

const deleteDraftStockDamage = async (localId) => {
  await db.delete(COLLECTIONS.STOCK_DAMAGES.name, localId);
};

const getSavedStockDamagesFromDB = async (targetShiftId) => {
  const currentShiftObj = ShiftHelper.getCurrentShiftId();
  const shiftId = targetShiftId != null ? targetShiftId : currentShiftObj?.id;
  const savedDamages = await ShiftHelper.getStockDamagesByShift(shiftId);

  const formattedData = savedDamages.map((item) => ({
    local_id: item.local_id,
    id: item.id || item.local_id,
    date: item.date ? DateTimeHelpers.getDate(item.date) : "-",
    productId: item.plu || item.productId || "-",
    name: item.name || "Unknown",
    price: item.price || 0,
    qty: Math.abs(item.qty || 0),
    unit: item.unit || "Unit",
    payable: item.payable || Math.abs(item.qty || 0) * (item.price || 0),
  }));

  return formattedData.sort((a, b) => (b.local_id || b.id) - (a.local_id || a.id));
};

const deleteStockDamageFromDB = async (targetKey) => {
  await db.delete(COLLECTIONS.STOCK_DAMAGES.name, targetKey);
};

const processStockDamageScanCode = (
  scannedCode,
  allProducts,
  quantity,
  setDamageItems,
  setSelectedProduct,
  setQuantity,
  productInputRef,
) => {
  if (!scannedCode) return;
  const matchedItem = (allProducts || []).find(
    (item) =>
      (item.ean && String(item.ean).toLowerCase() === scannedCode.toLowerCase()) ||
      (item.plu && String(item.plu).toLowerCase() === scannedCode.toLowerCase()) ||
      (item.name && item.name.toLowerCase() === scannedCode.toLowerCase()),
  );

  if (matchedItem) {
    const qty = Number(quantity) || 1;
    const newItem = {
      cartId: Date.now(),
      productId: matchedItem.id,
      plu: matchedItem.plu,
      name: matchedItem.name,
      qty: qty,
      cost: matchedItem.cost || 0,
      price: matchedItem.price,
      payable: (matchedItem.cost || 0) * qty,
      category: matchedItem.category,
    };

    setDamageItems((prev) => [...prev, newItem]);
    setSelectedProduct(null);
    setQuantity("");
    setTimeout(() => {
      productInputRef.current?.focus();
    }, 100);
  } else {
    toast.error(`Product not found for barcode: ${scannedCode}`);
  }
};

const StockDamageHelper = {
  saveStockDamageToCloudAndLocal,
  saveDraftStockDamage,
  deleteDraftStockDamage,
  getSavedStockDamagesFromDB,
  deleteStockDamageFromDB,
  processStockDamageScanCode,
};

export default StockDamageHelper;
